/** Minimum MCQ choices required to save or publish. */
export const MIN_MCQ_OPTIONS = 2;

/** Default number of blank options when creating a new MCQ. */
export const DEFAULT_NEW_MCQ_OPTION_COUNT = 4;

export interface McqOptionRow {
  id: string;
  text: string;
  imageUrl: string;
  audioUrl: string;
}

export function newMcqOptionId(): string {
  return crypto.randomUUID();
}

/** Blank template for a new MCQ (e.g. four empty rows). */
export function createEmptyMcqOptions(): McqOptionRow[] {
  return Array.from({ length: DEFAULT_NEW_MCQ_OPTION_COUNT }, () => ({
    id: newMcqOptionId(),
    text: "",
    imageUrl: "",
    audioUrl: "",
  }));
}

/** Text label for scoring / display from one stored options array entry. */
export function mcqOptionEntryText(entry: unknown): string {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object" && !Array.isArray(entry) && "text" in entry) {
    return String((entry as { text?: unknown }).text ?? "");
  }
  return "";
}

/** Optional media URLs from one stored entry (legacy string entries have none). */
export function mcqOptionEntryMedia(entry: unknown): { imageUrl: string | null; audioUrl: string | null } {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return { imageUrl: null, audioUrl: null };
  }
  const o = entry as Record<string, unknown>;
  const img = typeof o.imageUrl === "string" && o.imageUrl.trim() ? o.imageUrl.trim() : null;
  const aud = typeof o.audioUrl === "string" && o.audioUrl.trim() ? o.audioUrl.trim() : null;
  return { imageUrl: img, audioUrl: aud };
}

/** Build UI rows from API `options.options` (string[] or rich objects). */
export function parseMcqOptionsFromBackend(optsRaw: unknown): McqOptionRow[] {
  if (!Array.isArray(optsRaw) || optsRaw.length === 0) {
    return createEmptyMcqOptions();
  }
  return optsRaw.map((entry) => {
    if (typeof entry === "string") {
      return { id: newMcqOptionId(), text: entry, imageUrl: "", audioUrl: "" };
    }
    if (entry && typeof entry === "object" && !Array.isArray(entry)) {
      const o = entry as Record<string, unknown>;
      return {
        id: newMcqOptionId(),
        text: String(o.text ?? ""),
        imageUrl: typeof o.imageUrl === "string" ? o.imageUrl : "",
        audioUrl: typeof o.audioUrl === "string" ? o.audioUrl : "",
      };
    }
    return { id: newMcqOptionId(), text: "", imageUrl: "", audioUrl: "" };
  });
}

/** Build UI rows from stored `options.options` string array (any length). */
export function mapStringsToMcqOptions(optList: string[]): McqOptionRow[] {
  if (optList.length === 0) {
    return [
      { id: newMcqOptionId(), text: "", imageUrl: "", audioUrl: "" },
      { id: newMcqOptionId(), text: "", imageUrl: "", audioUrl: "" },
    ];
  }
  return optList.map((text) => ({
    id: newMcqOptionId(),
    text,
    imageUrl: "",
    audioUrl: "",
  }));
}

/**
 * Payload for `options` on create/patch question. Uses string[] when no per-option media (backward compatible).
 */
export function serializeMcqOptionsForApi(rows: McqOptionRow[]): { options: unknown[] } {
  const hasMedia = rows.some((r) => Boolean(r.imageUrl?.trim()) || Boolean(r.audioUrl?.trim()));
  if (!hasMedia) {
    return { options: rows.map((r) => r.text.trim()) };
  }
  return {
    options: rows.map((r) => ({
      text: r.text,
      imageUrl: r.imageUrl?.trim() ? r.imageUrl.trim() : null,
      audioUrl: r.audioUrl?.trim() ? r.audioUrl.trim() : null,
    })),
  };
}

/** Shuffled-order badge: A–Z, then 27, 28, … for more than 26 choices. */
export function mcqDisplayBadge(displayIndex: number): string {
  if (displayIndex >= 0 && displayIndex < 26) return String.fromCharCode(65 + displayIndex);
  return String(displayIndex + 1);
}
